import { toggleCoffeeCallAvailabilityFn } from "@/shared/api/talent.api";
import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import useThemeStore from "@/shared/stores/useThemeStore";
import { Talent, User } from "@/shared/types";
import { ROUTES } from "@/shared/constants";
import { navigateToRegisterTalent } from "@/shared/utils/navigateToRegisterTalent";
import { readOrganizationSession } from "@/modules/organization/organizationSession";
import { useCurrentOrganization } from "@/modules/organization/useCurrentOrganization";
import AddIcon from "@mui/icons-material/Add";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGetUserProfile, useUserLogout } from "../../api/user.api";
import GuestLiteModal from "../GuestLiteModal";
import CoffeeCallModeDialog, { CoffeeCallMode } from "../modal/CoffeeCallModDialog";
import {
  headerGoldLine,
  menuAccountCardSx,
  menuActiveBadgeSx,
  menuGoldBadgeSx,
  menuOutlineButtonSx,
  menuPrimaryButtonSx,
  menuSectionLabelSx,
  taasSwitchSx,
} from "./headerTaasStyles";

export const TalentUserToggleMenu = ({ talentListClose }: { talentListClose?: () => void }) => {
  const { userInfo, setUser } = useAuth();
  const { organization, isOrganizationWorkspace, enterOrganization, leaveOrganization } =
    useCurrentOrganization();
  const { update: logout } = useUserLogout({ key: ["user-logout"] });
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { SetTalentProfile, currentProfile } = useCurrentTalentProfile();
  const navigate = useNavigate();
  const [isCoffeeCallActive, setIsCoffeeCallActive] = React.useState(
    !!currentProfile?.coffe_call_enabled
  );
  const [isModeDialogOpen, setIsModeDialogOpen] = React.useState(false);
  const [pendingCoffeeCallChecked, setPendingCoffeeCallChecked] = React.useState<boolean | null>(
    null
  );
  const [isUpdatingCoffeeCall, setIsUpdatingCoffeeCall] = React.useState(false);
  const queryClient = useQueryClient();
  const isGuestLite = userInfo?.profile_completed === false;
  const [isGuestLiteModalOpen, setIsGuestLiteModalOpen] = useState(false);
  const allTalentsRef = React.useRef<Talent[]>(userInfo?.talent || []);
  const isCoffeeCallActiveRef = React.useRef<boolean>(isCoffeeCallActive);
  const { data: user, refetch } = useGetUserProfile({
    key: ["user-profile"],
    options: { enabled: false },
  });
  const { darkMode, toggleDarkMode } = useThemeStore();
  const { t } = useTranslation("header");

  const closeMenu = () => talentListClose?.();

  const userDisplayName =
    [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ") ||
    userInfo?.username ||
    userInfo?.email ||
    "";

  const isUserMode = !currentProfile && !isOrganizationWorkspace;
  const savedOrganization = organization ?? readOrganizationSession()?.organization ?? null;

  React.useEffect(() => {
    allTalentsRef.current = userInfo?.talent || [];
  }, [userInfo]);

  React.useEffect(() => {
    setIsCoffeeCallActive(!!currentProfile?.coffe_call_enabled);
    isCoffeeCallActiveRef.current = !!currentProfile?.coffe_call_enabled;
  }, [currentProfile]);

  const handleToggleCoffeeCall = async (checked: boolean) => {
    if (!currentProfile) return;
    if (checked) {
      setPendingCoffeeCallChecked(true);
      setIsModeDialogOpen(true);
      return;
    }
    try {
      setIsUpdatingCoffeeCall(true);
      await toggleCoffeeCallAvailabilityFn(currentProfile.id, {
        coffe_call_enabled: false,
      });
      SetTalentProfile({ ...currentProfile, coffe_call_enabled: false });
      isCoffeeCallActiveRef.current = false;
      setIsCoffeeCallActive(false);
      enqueueSnackbar(t("coffee_call_status_updated"), { variant: "success" });
      refetch();
      queryClient.invalidateQueries({ queryKey: ["talent", currentProfile?.id] });
    } catch {
      enqueueSnackbar(t("failed_to_update_status"), { variant: "error" });
    } finally {
      setIsUpdatingCoffeeCall(false);
    }
  };

  const closeModeDialog = () => {
    if (isUpdatingCoffeeCall) return;
    setIsModeDialogOpen(false);
    setPendingCoffeeCallChecked(null);
  };

  const handleDoneCoffeeCallMode = async (mode: CoffeeCallMode) => {
    if (!currentProfile || pendingCoffeeCallChecked === null) return;
    try {
      setIsUpdatingCoffeeCall(true);
      await toggleCoffeeCallAvailabilityFn(currentProfile.id, {
        coffe_call_enabled: pendingCoffeeCallChecked,
        coffee_call_mode: mode,
      });
      SetTalentProfile({
        ...currentProfile,
        coffe_call_enabled: pendingCoffeeCallChecked,
      });
      isCoffeeCallActiveRef.current = pendingCoffeeCallChecked;
      setIsCoffeeCallActive(pendingCoffeeCallChecked);
      enqueueSnackbar(t("coffee_call_status_updated"), { variant: "success" });
      setIsModeDialogOpen(false);
      setPendingCoffeeCallChecked(null);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["talent", currentProfile?.id] });
    } catch {
      enqueueSnackbar(t("failed_to_update_status"), { variant: "error" });
    } finally {
      setIsUpdatingCoffeeCall(false);
    }
  };

  const handleSignOut = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    localStorage.setItem("isGuestLite", "false");
    enqueueSnackbar(t("signing_you_out"), { variant: "info" });
    try {
      await logout({});
      setUser(null);
    } catch {
      enqueueSnackbar(t("failed_to_sign_out_try_again"), { variant: "error" });
      setIsLoggingOut(false);
      return;
    } finally {
      setIsLoggingOut(false);
    }
    setTimeout(() => {
      try {
        const savedLang = localStorage.getItem("lang");
        const savedTheme = localStorage.getItem("theme");
        localStorage.clear();
        sessionStorage.clear();
        if (savedLang) localStorage.setItem("lang", savedLang);
        if (savedTheme) localStorage.setItem("theme", savedTheme);
        enqueueSnackbar(t("signed_out_successfully"), { variant: "success" });
        window.location.replace("/");
      } catch {
        enqueueSnackbar(t("failed_to_sign_out_try_again"), { variant: "error" });
        setIsLoggingOut(false);
      }
    }, 600);
  };

  const switchToUser = () => {
    SetTalentProfile(null);
    leaveOrganization();
    closeMenu();
    navigate("/TNPage");
  };

  const switchToTalent = (talent: Talent) => {
    leaveOrganization();
    SetTalentProfile(talent);
    closeMenu();
    navigate("/creator-workspace");
    refetch();
    if (user) setUser(user?.data as User);
  };

  const switchToOrganization = () => {
    const session = readOrganizationSession();
    if (!session) {
      goCreateOrganization();
      return;
    }
    SetTalentProfile(null);
    enterOrganization(session);
    closeMenu();
    navigate(ROUTES.ORGANIZATION_HALL);
  };

  const goCreateOrganization = () => {
    if (isGuestLite) {
      setIsGuestLiteModalOpen(true);
      return;
    }
    closeMenu();
    navigate(ROUTES.REGISTER_ORGANIZATION);
  };

  const goRegisterTalent = () => {
    if (isGuestLite) {
      setIsGuestLiteModalOpen(true);
      return;
    }

    closeMenu();
    navigateToRegisterTalent();
  };

  const renderTalentList = () => {
    const allTalents = allTalentsRef.current;
    if (!allTalents.length) {
      return (
        <Box
          sx={{
            py: 2.5,
            px: 1.5,
            textAlign: "center",
            borderRadius: "12px",
            border: "none",
            bgcolor: "light-dark(#f7f7f7, #141414)",
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 36, color: "var(--text-muted)", mb: 1 }} />
          <Typography
            sx={{
              fontFamily: "var(--primary-font-family)",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--text)",
              mb: 0.5,
            }}
          >
            {t("no_talent_profiles_found")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "var(--primary-font-family)",
              color: "var(--text-muted)",
              display: "block",
              mb: 2,
            }}
          >
            {t("account_menu_talent_empty_hint")}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={goRegisterTalent}
            sx={menuPrimaryButtonSx}
          >
            {t("register_as_a")} {t("talent")}
          </Button>
        </Box>
      );
    }

    return (
      <Stack spacing={1}>
        {allTalents.map((talent) => {
          const selected = currentProfile?.id === talent.id;
          const displayName = talent.view_name || talent.username || t("talent");
          return (
            <Box key={talent.id}>
              <Box onClick={() => switchToTalent(talent)} sx={menuAccountCardSx(selected)}>
                <Avatar
                  src={talent.profile_image ?? ""}
                  alt={displayName}
                  sx={{
                    width: 44,
                    height: 44,
                    border: "none",
                  }}
                >
                  {displayName[0]}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: "var(--primary-font-family)",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "var(--text)",
                      }}
                    >
                      {displayName}
                    </Typography>
                    {selected && (
                      <CheckCircleIcon
                        sx={{ fontSize: 16, color: "var(--text)", flexShrink: 0 }}
                      />
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={0.75} mt={0.35}>
                    <Box component="span" sx={menuGoldBadgeSx}>
                      {t("talent")}
                    </Box>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontFamily: "var(--primary-font-family)",
                        color: "var(--text-muted)",
                      }}
                    >
                      @{talent.username}
                    </Typography>
                  </Stack>
                </Box>
                {selected && <Chip label={t("active_now")} size="small" sx={menuActiveBadgeSx} />}
              </Box>

              {selected && currentProfile && (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    mt: 1,
                    mx: 0.5,
                    px: 1.75,
                    py: 1.25,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    border: "none",
                    bgcolor: isCoffeeCallActiveRef.current
                      ? "light-dark(#f4f4f4, #1a1a1a)"
                      : "transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--primary-font-family)",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "var(--text)",
                    }}
                  >
                    {t("available_for_coffee_call")}
                  </Typography>
                  <Switch
                    checked={isCoffeeCallActive}
                    onChange={(e) => handleToggleCoffeeCall(e.target.checked)}
                    disabled={isUpdatingCoffeeCall}
                    sx={taasSwitchSx}
                  />
                </Box>
              )}
            </Box>
          );
        })}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={1}>
          {userInfo?.talent?.length === 0 && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={goRegisterTalent}
              sx={menuOutlineButtonSx}
            >
              {t("add_talent")}
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: "min(92vw, 360px)", sm: 380 },
        maxHeight: "min(85vh, 840px)",
        overflowY: "auto",
        fontFamily: "var(--primary-font-family)",
        color: "var(--text)",
      }}
    >
      <Box sx={headerGoldLine} />

      <Box sx={{ p: 2.25 }}>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
            fontSize: "1.15rem",
            fontWeight: 600,
            mb: 0.35,
            color: "var(--text)",
          }}
        >
          {t("account_menu_title")}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "var(--primary-font-family)",
            color: "var(--text-muted)",
            display: "block",
            mb: 2,
            lineHeight: 1.45,
          }}
        >
          {t("account_menu_subtitle")}
        </Typography>

        <Typography sx={menuSectionLabelSx}>{t("switch_to_user")}</Typography>
        <Box onClick={switchToUser} sx={menuAccountCardSx(isUserMode)}>
          <Avatar
            src={userInfo?.profile_image ?? ""}
            sx={{
              width: 44,
              height: 44,
              border: "none",
              bgcolor: "light-dark(#f4f4f4, #1a1a1a)",
            }}
          >
            {userDisplayName[0]?.toUpperCase() ?? "U"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontFamily: "var(--primary-font-family)",
                fontWeight: 700,
                fontSize: "0.875rem",
                color: "var(--text)",
              }}
            >
              {userDisplayName}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75} mt={0.35}>
              <Box component="span" sx={menuGoldBadgeSx}>
                {t("main_account")}
              </Box>
              {userInfo?.username && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ fontFamily: "var(--primary-font-family)", color: "var(--text-muted)" }}
                >
                  @{userInfo.username}
                </Typography>
              )}
            </Stack>
          </Box>
          {isUserMode && <Chip label={t("active_now")} size="small" sx={menuActiveBadgeSx} />}
        </Box>

        {userInfo?.account_type === "User" && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={menuSectionLabelSx}>{t("organization_profiles")}</Typography>
            {savedOrganization ? (
              <Box
                onClick={switchToOrganization}
                sx={menuAccountCardSx(isOrganizationWorkspace)}
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    border: "none",
                    bgcolor: "light-dark(#f4f4f4, #1a1a1a)",
                  }}
                >
                  <ApartmentIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: "var(--primary-font-family)",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      color: "var(--text)",
                    }}
                  >
                    {savedOrganization.name}
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={0.75} mt={0.35}>
                    <Box component="span" sx={menuGoldBadgeSx}>
                      {t("organization")}
                    </Box>
                  </Stack>
                </Box>
                {isOrganizationWorkspace && (
                  <Chip label={t("active_now")} size="small" sx={menuActiveBadgeSx} />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  py: 2.5,
                  px: 1.5,
                  textAlign: "center",
                  borderRadius: "12px",
                  border: "none",
                  bgcolor: "light-dark(#f7f7f7, #141414)",
                }}
              >
                <ApartmentIcon sx={{ fontSize: 36, color: "var(--text-muted)", mb: 1 }} />
                <Typography
                  sx={{
                    fontFamily: "var(--primary-font-family)",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "var(--text)",
                    mb: 0.5,
                  }}
                >
                  {t("no_organization_found")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "var(--primary-font-family)",
                    color: "var(--text-muted)",
                    display: "block",
                    mb: 2,
                  }}
                >
                  {t("account_menu_organization_empty_hint")}
                </Typography>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<AddIcon />}
                  onClick={goCreateOrganization}
                  sx={menuPrimaryButtonSx}
                >
                  {t("create_organization")}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {userInfo?.account_type !== "Business" && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={menuSectionLabelSx}>{t("talent_profiles")}</Typography>
            {renderTalentList()}
          </Box>
        )}

        <Divider
          sx={{
            my: 2,
            border: "none",
            height: 0,
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 0.5,
            py: 0.75,
          }}
        >
          <Typography
            sx={{
              fontFamily: "var(--primary-font-family)",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "var(--text)",
            }}
          >
            {t("dark_mode")}
          </Typography>
          <Switch
            checked={darkMode}
            onChange={toggleDarkMode}
            inputProps={{
              "aria-label": `${t("light_mode")} / ${t("dark_mode")}`,
            }}
            sx={taasSwitchSx}
          />
        </Box>

        <Stack direction="row" spacing={1} mt={1.5}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<SettingsOutlinedIcon />}
            onClick={() => {
              closeMenu();
              navigate("/settings");
            }}
            sx={menuOutlineButtonSx}
          >
            {currentProfile ? t("talent_settings_link") : t("user_settings_link")}
          </Button>
        </Stack>

        <Button
          fullWidth
          startIcon={
            isLoggingOut ? (
              <CircularProgress size={16} sx={{ color: "var(--text)" }} />
            ) : (
              <LogoutIcon />
            )
          }
          onClick={handleSignOut}
          disabled={isLoggingOut}
          sx={{
            mt: 1.25,
            fontFamily: "var(--primary-font-family)",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            justifyContent: "center",
            borderRadius: "10px",
            py: 1.1,
            color: "var(--destructive-600)",
            border: "none",
            "&:hover": {
              bgcolor: "color-mix(in srgb, var(--destructive-600) 12%, transparent)",
            },
          }}
        >
          {isLoggingOut ? t("signing_out") : t("logout")}
        </Button>
      </Box>

      <CoffeeCallModeDialog
        open={isModeDialogOpen}
        loading={isUpdatingCoffeeCall}
        onClose={closeModeDialog}
        onDone={handleDoneCoffeeCallMode}
      />
      <GuestLiteModal open={isGuestLiteModalOpen} onClose={() => setIsGuestLiteModalOpen(false)} />
    </Box>
  );
};
